import { useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Full-screen lightbox for the villa gallery.
 *
 * Props:
 *   images  — ordered array of { src, alt }
 *   index   — currently-visible image index, or -1 when closed
 *   onClose — called when the user dismisses (ESC, backdrop, X button)
 *   onNav   — called with the new index when the user navigates (← / → / arrows)
 *
 * Design decisions:
 * - Solid near-black backdrop (rgba 0,0,0,0.94) matches the site's dark ionian
 *   tone; keeps the villa photograph as the hero, not the chrome.
 * - Body scroll is locked while the lightbox is open — otherwise the underlying
 *   grid can wheel-jitter behind the modal on trackpads.
 * - `role="dialog"` + focus-trap-less ESC handler = accessible without pulling
 *   a whole a11y library in for one screen.
 */
export default function Lightbox({ images, index, onClose, onNav }) {
  const isOpen = index >= 0 && index < images.length;
  const img = isOpen ? images[index] : null;

  const goPrev = useCallback(() => {
    if (!isOpen) return;
    onNav((index - 1 + images.length) % images.length);
  }, [isOpen, index, images.length, onNav]);

  const goNext = useCallback(() => {
    if (!isOpen) return;
    onNav((index + 1) % images.length);
  }, [isOpen, index, images.length, onNav]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    document.addEventListener("keydown", onKey);
    // Lock underlying scroll so mobile pinch-zoom and desktop scroll don't
    // shift the page while inspecting a photo.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose, goPrev, goNext]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.94)" }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={img.alt || "Photo"}
      data-testid="lightbox"
    >
      {/* Close */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close photo"
        data-testid="lightbox-close"
        className="absolute top-4 right-4 md:top-6 md:right-8 text-white/80 hover:text-white transition p-2"
      >
        <X className="h-6 w-6" strokeWidth={1.2} />
      </button>

      {/* Prev */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          aria-label="Previous photo"
          data-testid="lightbox-prev"
          className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition p-2"
        >
          <ChevronLeft className="h-8 w-8 md:h-10 md:w-10" strokeWidth={1.2} />
        </button>
      )}

      {/* Image + caption */}
      <div
        className="relative max-w-[92vw] max-h-[88vh] flex flex-col items-center gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={img.src}
          alt={img.alt || ""}
          className="max-w-full max-h-[82vh] object-contain shadow-2xl"
          data-testid="lightbox-image"
        />
        {img.alt && (
          <div className="text-white/60 text-xs md:text-sm tracking-wide text-center max-w-2xl px-4">
            {img.alt}
          </div>
        )}
        {images.length > 1 && (
          <div className="text-white/40 text-[10px] tracking-widest uppercase">
            {index + 1} / {images.length}
          </div>
        )}
      </div>

      {/* Next */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          aria-label="Next photo"
          data-testid="lightbox-next"
          className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition p-2"
        >
          <ChevronRight className="h-8 w-8 md:h-10 md:w-10" strokeWidth={1.2} />
        </button>
      )}
    </div>
  );
}
